/**
 * Auth Navigation Utilities
 * Centralized logic for post-authentication navigation
 */

import { appRegistry } from '@commander/utils';

interface CompanyEmployee {
  app_access: string[];
}

interface User {
  onboarding_complete?: boolean;
  joyride_viewed?: boolean;
  company_employee?: CompanyEmployee;
  user_products?: Array<{
    product: {
      name: string;
    };
  }>;
}

interface NavigationResult {
  path: string;
  reason: string;
}

/**
 * Determines the correct navigation path after successful authentication
 * Based on user's onboarding status, products, and joyride completion
 */
export function getPostLoginRoute(user: User): NavigationResult {
  const appAccess = user.company_employee?.app_access || [];

  if (appAccess.length === 1 && appAccess[0] === 'driver') {
    const driverUrl = appRegistry.getAppUrl('driver');

    return {
      path: driverUrl || '/driver-routing',
      reason: 'driver_only_access',
    };
  }

  // Check onboarding status first
  if (!user.onboarding_complete) {
    return {
      path: '/onboarding',
      reason: 'onboarding_incomplete',
    };
  }

  // Check if user has only prospecting access
  if (appAccess.length === 1 && appAccess[0] === 'prospecting') {
    const homeUrl = appRegistry.getAppUrl('home', '/dashboard');
    // Check if homeUrl is not the fallback value (i.e., home app exists)
    if (homeUrl && homeUrl !== '/dashboard') {
      return {
        path: homeUrl,
        reason: 'prospecting_only_access',
      };
    }
    return {
      path: '/dashboard',
      reason: 'prospecting_only_access',
    };
  }

  // Check if user has any products
  const userProducts = user.user_products || [];
  if (userProducts.length === 0) {
    return {
      path: '/no-active-product',
      reason: 'no_products',
    };
  }

  // Get product names
  const productNames = userProducts.map((p) => p.product.name);

  // Navigate based on primary product
  if (productNames.includes('Commander AI')) {
    if (!user.joyride_viewed) {
      const homeUrl = appRegistry.getAppUrl('home', '/dashboard');
      // Check if homeUrl is not the fallback value (i.e., home app exists)
      if (homeUrl && homeUrl !== '/dashboard') {
        return {
          path: homeUrl,
          reason: 'joyride_pending',
        };
      }
      return {
        path: '/dashboard',
        reason: 'joyride_pending',
      };
    }
    return {
      path: '/dashboard',
      reason: 'commander_ai_access',
    };
  }

  if (productNames.includes('Commander Expo')) {
    return {
      // LIKELY DEAD: 2026-10-11 — /expo/home targets a route no app defines (dead Expo feature);
      // unknown — needs investigation. See docs/cleanup-backlog.md.
      path: '/expo/home',
      reason: 'commander_expo_access',
    };
  }

  // Default fallback
  return {
    path: '/dashboard',
    reason: 'default',
  };
}

/**
 * Whether the user holds the prospecting-only seat: Prospecting access without CRM access.
 * Mirrors the backend's `resolveSeat` (src/services/actionDash/seat.js), which decides which
 * app's pages that user's links point at.
 */
export function hasProspectingWithoutCrm(appAccess: string[] | null | undefined): boolean {
  const apps = appAccess ?? [];
  return apps.includes('prospecting') && !apps.includes('crm');
}

/**
 * Clears URL parameters after processing (e.g., social login tokens)
 * This prevents tokens from remaining in browser history
 */
export function clearURLParams(
  navigate: (path: string, options?: any) => void,
  currentPath: string,
  targetPath?: string
) {
  if (targetPath) {
    // If targetPath is provided, navigate directly to it with replace
    navigate(targetPath, { replace: true });
  } else {
    // Default behavior: navigate to currentPath with replace
    navigate(currentPath, { replace: true });
  }
}
